import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Page } from 'react-pdf';
import { Document as ReactPDFViewer } from 'react-pdf/dist/esm/entry.webpack';
import { TokenSession } from '@avangard/utils/token';
import { gray, red, white } from '@avangard/ui/colors';
import { breakpoints } from '@avangard/ui/utils';
import { Button, Loader } from '@avangard/ui/core';
import { ButtonGroup } from '@material-ui/core';

export type DocumentPDF = {
    id: string;
    name: string;
    urlForView: string;
};

export type PDFViewerProps = {
    initialPDF?: DocumentPDF | null;
    PDFs: DocumentPDF[];
};

const Root = styled.div<{ loaded?: boolean; hasButtons?: boolean }>`
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => (!!p.loaded ? gray[60] : 'none')};
    margin: 24px -32px -16px;
    padding: 24px;
    position: relative;
    z-index: 0;
    min-height: calc(100dvh - (280px));

    ${breakpoints.down('xs')} {
        margin: 16px -16px -8px;
        padding: ${p => (p.hasButtons ? '60px 16px 16px' : '16px')};
    }
`;

const StyledPage = styled(Page)`
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const StyledLoader = styled(Loader)`
    background: none;
`;

const StyledButtonGroup = styled(ButtonGroup)`
    z-index: 2;
    position: absolute;
    left: 0;
    top: 16px;
    overflow-y: hidden;
    overflow-x: auto;
    width: 100%;
    padding-bottom: 10px;
    padding-right: 16px;
    padding-left: 16px;

    > * {
        flex: 0 0 auto;
        text-overflow: ellipsis;
        white-space: nowrap;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        &:first-child {
            margin-inline-start: 16px;
        }
    }
`;

const StyledButtonGroupItem = styled(Button)<{ selected: boolean }>(({ selected }) => ({
    height: '32px',
    fontSize: '1.4rem',
    color: red[100],
    borderColor: red[100],
    background: selected ? red[20] : white[100],
    flex: '0 0 auto',

    '&:hover': {
        background: red[20],
    },
}));

const maxWidthPage = 1280;

const PDFViewer = (props: PDFViewerProps): JSX.Element => {
    const { initialPDF, PDFs } = props;

    const { t } = useTranslation('documents');
    const viewerRef = React.useRef<HTMLDivElement>(null);

    const [numPages, setNumPages] = React.useState<number>(0);
    const [currentDocumentFile, setCurrentDocumentFile] = React.useState<DocumentPDF | null>(
        initialPDF || null,
    );

    const [pageWidth, setPageWidth] = React.useState<number | undefined>(undefined);

    const hasButtons = numPages > 0 && PDFs.length > 1;

    const accessToken = TokenSession.getCurrentSession().getAccessToken().getToken();

    React.useEffect(() => {
        if (initialPDF) {
            setCurrentDocumentFile(initialPDF);
        } else {
            setCurrentDocumentFile(PDFs?.[0]);
        }
    }, [initialPDF, PDFs]);

    React.useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            const width = viewerRef.current?.clientWidth || 0;

            if (width < maxWidthPage) {
                setPageWidth(width * 0.9);
            } else {
                setPageWidth(maxWidthPage);
            }
        });

        if (viewerRef?.current) {
            resizeObserver.observe(viewerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [viewerRef]);

    const handleDocumentLoadSuccess = (pdf: { numPages: number }) => {
        setNumPages(pdf.numPages);
    };

    return (
        <Root loaded={numPages > 0} hasButtons={hasButtons} ref={viewerRef}>
            {hasButtons && (
                <StyledButtonGroup variant='outlined'>
                    {PDFs.map(documentFile => {
                        const handleChooseDocument = (documentFile: DocumentPDF) => () => {
                            // после выбора отработает handleDocumentLoadSuccess и подставит нормальное кол-во страниц
                            setNumPages(1);
                            setCurrentDocumentFile(documentFile);
                        };

                        return (
                            <StyledButtonGroupItem
                                key={documentFile?.id}
                                selected={currentDocumentFile?.id === documentFile?.id}
                                onClick={handleChooseDocument(documentFile)}
                            >
                                {documentFile?.name}
                            </StyledButtonGroupItem>
                        );
                    })}
                </StyledButtonGroup>
            )}

            {currentDocumentFile && (
                <ReactPDFViewer
                    file={{
                        url: `${currentDocumentFile?.urlForView}?p=center`,
                        httpHeaders: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    loading={<StyledLoader />}
                    renderMode='svg'
                    noData={t('documents:pdf_viewer.no_data') ?? ''}
                    error={t('documents:pdf_viewer.error') ?? ''}
                    options={{
                        verbosity: 0,
                    }}
                >
                    {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber: number) => {
                        return (
                            <StyledPage
                                width={pageWidth}
                                key={`page_${pageNumber}`}
                                renderMode='svg'
                                renderAnnotationLayer={false}
                                renderInteractiveForms={false}
                                pageNumber={pageNumber}
                            />
                        );
                    })}
                </ReactPDFViewer>
            )}
        </Root>
    );
};

export { PDFViewer };
